import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateInNoteRequest,
    CreateOutNoteRequest,
    ListLotsParams,
    ListNotesParams,
    UpdateInNoteRequest,
    UpdateOutNoteRequest,
} from '@/lib/api/agri-material-lot-api'
import { agriMaterialLotApi } from '@/lib/api/agri-material-lot-api'
import { m } from '@/lib/paraglide/messages'

// ============================================================================
// Query keys
// ============================================================================

export const agriLotKeys = {
    all: ['agri-material-lots'] as const,
    lots: () => [...agriLotKeys.all, 'lots'] as const,
    lotList: (params: ListLotsParams) =>
        [...agriLotKeys.lots(), 'list', params] as const,
    lotDetail: (id: string) => [...agriLotKeys.lots(), 'detail', id] as const,

    inNotes: () => [...agriLotKeys.all, 'in-notes'] as const,
    inNoteList: (params: ListNotesParams) =>
        [...agriLotKeys.inNotes(), 'list', params] as const,
    inNoteDetail: (id: string) =>
        [...agriLotKeys.inNotes(), 'detail', id] as const,

    outNotes: () => [...agriLotKeys.all, 'out-notes'] as const,
    outNoteList: (params: ListNotesParams) =>
        [...agriLotKeys.outNotes(), 'list', params] as const,
    outNoteDetail: (id: string) =>
        [...agriLotKeys.outNotes(), 'detail', id] as const,
}

// ============================================================================
// Lot queries
// ============================================================================

export function useAgriLots(
    params: ListLotsParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: agriLotKeys.lotList(params),
        queryFn: () => agriMaterialLotApi.listLots(params),
    })
}

export function useAgriLotsInfinite(
    params: Omit<ListLotsParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...agriLotKeys.lots(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            agriMaterialLotApi.listLots({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useAgriLot(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: agriLotKeys.lotDetail(id ?? ''),
        queryFn: () => agriMaterialLotApi.getLot(id as string),
    })
}

export function useAgriLotHistory(id: string | undefined) {
    return useInfiniteQuery({
        enabled: !!id,
        queryKey: [...agriLotKeys.lots(), 'history', id ?? ''] as const,
        queryFn: ({ pageParam }) =>
            agriMaterialLotApi.getLotHistory(id as string, {
                page: pageParam,
                per_page: 20,
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

// ============================================================================
// Stock-in note (PNK) queries
// ============================================================================

export function useAgriInNotes(
    params: ListNotesParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: agriLotKeys.inNoteList(params),
        queryFn: () => agriMaterialLotApi.listInNotes(params),
    })
}

export function useAgriInNotesInfinite(
    params: Omit<ListNotesParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...agriLotKeys.inNotes(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            agriMaterialLotApi.listInNotes({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useAgriInNote(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: agriLotKeys.inNoteDetail(id ?? ''),
        queryFn: () => agriMaterialLotApi.getInNote(id as string),
    })
}

export function useCreateAgriInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: CreateInNoteRequest) =>
            agriMaterialLotApi.createInNote(req),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: agriLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: agriLotKeys.lots() })
            toast.success(m.aml_toast_create_pnk())
        },
    })
}

export function useUpdateAgriInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, req }: { id: string; req: UpdateInNoteRequest }) =>
            agriMaterialLotApi.updateInNote(id, req),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: agriLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: agriLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: agriLotKeys.lots() })
            toast.success(m.aml_toast_update_pnk())
        },
    })
}

export function useApproveAgriInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => agriMaterialLotApi.approveInNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: agriLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: agriLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: agriLotKeys.lots() })
            toast.success(m.aml_toast_approve_pnk())
        },
    })
}

export function useRejectAgriInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) =>
            agriMaterialLotApi.rejectInNote(id, reason),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: agriLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: agriLotKeys.inNotes() })
            toast.success(m.aml_toast_reject_pnk())
        },
    })
}

export function useCancelAgriInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => agriMaterialLotApi.cancelInNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: agriLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: agriLotKeys.inNotes() })
            toast.success(m.aml_toast_cancel_pnk())
        },
    })
}

/** Rollback PNK đã duyệt: BE sinh PXK điều chỉnh trừ lại tồn (M-17). */
export function useRollbackAgriInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => agriMaterialLotApi.rollbackInNote(id),
        onSuccess: (res, id) => {
            qc.invalidateQueries({ queryKey: agriLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: agriLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: agriLotKeys.outNotes() })
            qc.invalidateQueries({ queryKey: agriLotKeys.lots() })
            toast.success(
                m.note_rollback_success({ code: res.data?.code ?? '' }),
            )
        },
    })
}

// ============================================================================
// Stock-out note (PXK) queries
// ============================================================================

/** Rollback PXK đã duyệt: BE sinh PNK điều chỉnh cộng lại tồn (M-17). */
export function useRollbackAgriOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => agriMaterialLotApi.rollbackOutNote(id),
        onSuccess: (res, id) => {
            qc.invalidateQueries({ queryKey: agriLotKeys.outNoteDetail(id) })
            qc.invalidateQueries({ queryKey: agriLotKeys.outNotes() })
            qc.invalidateQueries({ queryKey: agriLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: agriLotKeys.lots() })
            toast.success(
                m.note_rollback_success({ code: res.data?.code ?? '' }),
            )
        },
    })
}

export function useAgriOutNotes(
    params: ListNotesParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: agriLotKeys.outNoteList(params),
        queryFn: () => agriMaterialLotApi.listOutNotes(params),
    })
}

export function useAgriOutNotesInfinite(
    params: Omit<ListNotesParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...agriLotKeys.outNotes(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            agriMaterialLotApi.listOutNotes({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useAgriOutNote(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: agriLotKeys.outNoteDetail(id ?? ''),
        queryFn: () => agriMaterialLotApi.getOutNote(id as string),
    })
}

export function useCreateAgriOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: CreateOutNoteRequest) =>
            agriMaterialLotApi.createOutNote(req),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: agriLotKeys.outNotes() })
            toast.success(m.aml_toast_create_pxk())
        },
    })
}

export function useUpdateAgriOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, req }: { id: string; req: UpdateOutNoteRequest }) =>
            agriMaterialLotApi.updateOutNote(id, req),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: agriLotKeys.outNoteDetail(id) })
            qc.invalidateQueries({ queryKey: agriLotKeys.outNotes() })
            qc.invalidateQueries({ queryKey: agriLotKeys.lots() })
            toast.success(m.aml_toast_update_pxk())
        },
    })
}

export function useApproveAgriOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => agriMaterialLotApi.approveOutNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: agriLotKeys.outNoteDetail(id) })
            qc.invalidateQueries({ queryKey: agriLotKeys.outNotes() })
            qc.invalidateQueries({ queryKey: agriLotKeys.lots() })
            toast.success(m.aml_toast_approve_pxk())
        },
    })
}

export function useRejectAgriOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) =>
            agriMaterialLotApi.rejectOutNote(id, reason),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: agriLotKeys.outNoteDetail(id) })
            qc.invalidateQueries({ queryKey: agriLotKeys.outNotes() })
            toast.success(m.aml_toast_reject_pxk())
        },
    })
}

export function useCancelAgriOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => agriMaterialLotApi.cancelOutNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: agriLotKeys.outNoteDetail(id) })
            qc.invalidateQueries({ queryKey: agriLotKeys.outNotes() })
            toast.success(m.aml_toast_cancel_pxk())
        },
    })
}
